// ============================================
// Path: /frontend/src/pages/PaymentStatusPage.jsx
// Purpose: Monitor and display subscription payment status
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import api from '../api/client';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';

export default function PaymentStatusPage() {
  console.log('[PaymentStatus] Component mounted');

  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [status, setStatus] = useState('PENDING'); // PENDING, COMPLETED, FAILED, TIMEOUT
  const [statusMessage, setStatusMessage] = useState('Waiting for payment confirmation...');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(120); // 2 minutes timeout
  
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  useEffect(() => {

      if (pollIntervalRef.current || countdownIntervalRef.current) {
      console.log('[PaymentStatus] Already polling, skipping');
      return;
    }

    // Get payment details from navigation state or sessionStorage
    const detailsFromState = location.state;
    const detailsFromStorage = sessionStorage.getItem('paymentDetails');

    let details = null;
    if (detailsFromStorage) {
      details = JSON.parse(detailsFromStorage);
    } else if (detailsFromState?.paymentId) {
      details = detailsFromState;
    }

    console.log('[PaymentStatus] Details from state:', detailsFromState);
    console.log('[PaymentStatus] Details from storage:', detailsFromStorage);
    console.log('[PaymentStatus] Final details:', details)

    if (!details || !details.paymentId) {
      console.log('[PaymentStatus] No payment details found');
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No payment information found.',
      });
      navigate('/subscription/upgrade');
      return;
    }

    console.log('[PaymentStatus] Payment details loaded:', details);
    setPaymentDetails(details);

    // Start polling for payment status
    startPolling(details.paymentId);

    // Start countdown timer
    startCountdown();

return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
 }, []);

  const startPolling = (paymentId) => {
    console.log('[PaymentStatus] Starting payment status polling');

    // Poll immediately
    checkPaymentStatus(paymentId);

    // Then poll every 45 seconds (to avoid Safaricom bot blocking)
    pollIntervalRef.current = setInterval(() => {
      checkPaymentStatus(paymentId);
    }, 45000);
  };

  const startCountdown = () => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const checkPaymentStatus = async (paymentId) => {
    try {
      const response = await api.get(`/payments/${paymentId}/status`);
      const { status: paymentStatus, data } = response.data;

      console.log('[PaymentStatus] Status check:', paymentStatus);

      if (paymentStatus === 'COMPLETED') {
        handleSuccess(data);
      } else if (paymentStatus === 'FAILED') {
        handleFailure(data?.failureReason || 'Payment failed');
      }
      // Continue polling if still PENDING
    } catch (error) {
      console.error('[PaymentStatus] Status check error:', error);
      // Don't stop polling on error, might be temporary network issue
    }
  };

const handleSuccess = async (data) => {
  console.log('[PaymentStatus] Payment successful:', data);
  
  setStatus('COMPLETED');

  // ⭐ Check payment type from stored details
  const isPromotion = paymentDetails?.type === 'PROMOTION';
  const isReactivation = paymentDetails?.type === 'LISTING_REACTIVATION';
  // ⭐ FIX: Detect subscription upgrade - check for "tier" field OR type === 'SUBSCRIPTION_UPGRADE'
  const isRenewal = paymentDetails?.type === 'SUBSCRIPTION_UPGRADE' || 
                  (paymentDetails?.tier && !paymentDetails?.type);
  const isListingCreation = paymentDetails?.type === 'LISTING_CREATION';

  // Clear polling intervals
  if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

  // ============================================
  // CASE 1: LISTING REACTIVATION
  // ============================================
  if (isReactivation) {
    setStatusMessage('Payment completed! Reactivating your listing...');
    
    toast({
      title: 'Subscription Renewed!',
      description: 'Your listing will be reactivated shortly.',
    });

    // ⭐ REFRESH USER DATA - subscription renewed
    await refreshUser();
    
    // Clear session storage AFTER refresh
    sessionStorage.removeItem('paymentDetails');
    sessionStorage.removeItem('reactivatingListingId');
    sessionStorage.removeItem('selectedPlan');
    sessionStorage.removeItem('pendingListing');

    setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 2000);
    
    return;
  }

  // ============================================
  // CASE 2: PROMOTION PAYMENT
  // ============================================
  if (isPromotion) {
    setStatusMessage('Payment completed! Your listing is now promoted.');
    
    toast({
      title: 'Listing Promoted!',
      description: 'Your listing will appear at the top of search results.',
    });

    sessionStorage.removeItem('paymentDetails');

    setTimeout(() => {
      navigate('/dashboard', { replace: true });
    }, 2000);
    
    return;
  }

// ============================================
// CASE 3: SUBSCRIPTION UPGRADE
// ============================================

if (isRenewal) {
  console.log('[PaymentStatus] 🔄 Processing subscription upgrade payment');
  
  // ⭐ FIX: Declare ALL variables at the top of the block
  const isUpgradingForListing = sessionStorage.getItem('upgradingForListing');
  const pendingListingStr = sessionStorage.getItem('pendingListing');
  const formDataStr = sessionStorage.getItem('createListingFormData');
  
  // Check if user upgraded to bypass tier limit and create listing
  if (isUpgradingForListing && pendingListingStr) {
    console.log('[PaymentStatus] ✅ User upgraded to bypass listing limit - creating listing now');
    
    setStatusMessage('Payment completed! Creating your listing...');
    
    try {
      const pendingListing = JSON.parse(pendingListingStr);
      const formData = formDataStr ? JSON.parse(formDataStr) : null;

      console.log('[PaymentStatus] Creating listing after upgrade:', pendingListing);

      // Upload images from form data
      let uploadedImageUrls = [];

      if (formData?.imagePreviews && formData.imagePreviews.length > 0) {
        console.log('[PaymentStatus] Uploading', formData.imagePreviews.length, 'images...');
        
        const imageFiles = await Promise.all(
          formData.imagePreviews.map(async (base64, index) => {
            const response = await fetch(base64);
            const blob = await response.blob();
            return new File([blob], `listing-image-${index}.jpg`, { type: 'image/jpeg' });
          })
        );

        const uploadPromises = imageFiles.map(async (file) => {
          const formData = new FormData();
          formData.append('image', file);
          
          const uploadResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/upload/image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error('Image upload failed');
          }

          const result = await uploadResponse.json();
          return result.url;
        });

        uploadedImageUrls = await Promise.all(uploadPromises);
        console.log('[PaymentStatus] Images uploaded:', uploadedImageUrls);
      }

      // Create the listing
      const listingData = {
        ...pendingListing,
        imageUrl: uploadedImageUrls,
        plan: paymentDetails.tier
      };

      console.log('[PaymentStatus] Submitting listing to API...');

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(listingData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create listing');
      }

      const result = await response.json();
      console.log('[PaymentStatus] Listing created successfully:', result);

      // Clear all saved data
      sessionStorage.removeItem('pendingListing');
      sessionStorage.removeItem('createListingFormData');
      sessionStorage.removeItem('paymentDetails');
      sessionStorage.removeItem('upgradingForListing');
      sessionStorage.removeItem('selectedPlan');

      // Refresh user data
      await refreshUser();

      toast({
        title: 'Listing Created!',
        description: 'Your listing has been submitted for admin approval.',
      });

      setStatusMessage('Listing created successfully! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

      return;

    } catch (error) {
      console.error('[PaymentStatus] Failed to create listing after upgrade:', error);
      
      toast({
        variant: 'destructive',
        title: 'Listing Creation Failed',
        description: error.message || 'Please contact support with your payment receipt.',
      });

      setStatusMessage('Upgrade successful, but listing creation failed. Please contact support.');
      setStatus('FAILED');
      return;
    }
  }

  // ⭐ Check if user is upgrading TO CREATE A LISTING (from checkout flow)
    
    if (pendingListingStr) {
      const parsed = JSON.parse(pendingListingStr);
    
    // 🔧 FIX: Check if this is just a direct subscription (renewal flag) or actual listing data
    if (parsed.isRenewal || parsed.isReactivation) {

      // This is FLOW 2: Direct subscription upgrade, no listing to create
      setStatusMessage('Payment completed! Your subscription has been upgraded.');
      
      await refreshUser();
      
      toast({
        title: 'Upgrade Successful',
        description: 'Your subscription has been upgraded successfully!',
      });

      sessionStorage.removeItem('paymentDetails');
      sessionStorage.removeItem('pendingListing');
      sessionStorage.removeItem('renewingSubscription');
      sessionStorage.removeItem('upgradePlan');
      sessionStorage.removeItem('selectedPlan');
      sessionStorage.removeItem('comingFromUpgrade');
      sessionStorage.removeItem('upgradingForListing');

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);
      
      return;
    }
    
    // CASE 3A: FLOW 1 - Upgrade WITH pending listing → Create listing after upgrade
    setStatusMessage('Payment completed! Creating your listing...');

    try {
      // Get pending listing data
      const pendingListingStr = sessionStorage.getItem('pendingListing');
      const formDataStr = sessionStorage.getItem('createListingFormData');

      if (!pendingListingStr) {
        throw new Error('No pending listing found');
      }

      const pendingListing = JSON.parse(pendingListingStr);
      const formData = formDataStr ? JSON.parse(formDataStr) : null;

      console.log('[PaymentStatus] Creating listing after upgrade:', pendingListing);

      // Upload images from form data
      let uploadedImageUrls = [];

      if (formData?.imagePreviews && formData.imagePreviews.length > 0) {
        console.log('[PaymentStatus] Uploading', formData.imagePreviews.length, 'images...');
        
        // Convert base64 to File objects
        const imageFiles = await Promise.all(
          formData.imagePreviews.map(async (base64, index) => {
            const response = await fetch(base64);
            const blob = await response.blob();
            return new File([blob], `listing-image-${index}.jpg`, { type: 'image/jpeg' });
          })
        );

        // Upload each image
        const uploadPromises = imageFiles.map(async (file) => {
          const formData = new FormData();
          formData.append('image', file);
          
          const uploadResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/upload/image`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          });

          if (!uploadResponse.ok) {
            throw new Error('Image upload failed');
          }

          const result = await uploadResponse.json();
          return result.url;
        });

        uploadedImageUrls = await Promise.all(uploadPromises);
        console.log('[PaymentStatus] Images uploaded:', uploadedImageUrls);
      }

      // Create the listing with uploaded images
      const listingData = {
        ...pendingListing,
        imageUrl: uploadedImageUrls,
        plan: paymentDetails.tier
      };

      console.log('[PaymentStatus] Submitting listing to API...');

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(listingData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create listing');
      }

      const result = await response.json();
      console.log('[PaymentStatus] Listing created successfully:', result);

      // Clear all saved data
      sessionStorage.removeItem('pendingListing');
      sessionStorage.removeItem('createListingFormData');
      sessionStorage.removeItem('paymentDetails');
      sessionStorage.removeItem('upgradePlan');
      sessionStorage.removeItem('selectedPlan');
      sessionStorage.removeItem('comingFromUpgrade');
      sessionStorage.removeItem('upgradingForListing');

      // Refresh user data
      await refreshUser();

      // Show success and redirect
      toast({
        title: 'Listing Created!',
        description: 'Your listing has been submitted for admin approval.',
      });

      setStatusMessage('Listing created successfully! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

    } catch (error) {
      console.error('[PaymentStatus] Failed to create listing after upgrade:', error);
      
      toast({
        variant: 'destructive',
        title: 'Listing Creation Failed',
        description: error.message || 'Please contact support with your payment receipt.',
      });

      setStatusMessage('Upgrade successful, but listing creation failed. Please contact support.');
      setStatus('FAILED');
    }

    return;
  }
  
  // CASE 3B: Upgrade WITHOUT pending listing → Just upgrade subscription
  setStatusMessage('Payment completed! Your subscription has been upgraded.');
  
  await refreshUser();
  
  toast({
    title: 'Upgrade Successful',
    description: 'Your subscription has been upgraded successfully!',
  });

  sessionStorage.removeItem('paymentDetails');
  sessionStorage.removeItem('pendingListing');
  sessionStorage.removeItem('renewingSubscription');
  sessionStorage.removeItem('upgradePlan');
  sessionStorage.removeItem('selectedPlan');
  sessionStorage.removeItem('comingFromUpgrade');
  sessionStorage.removeItem('upgradingForListing');

  setTimeout(() => {
    navigate('/dashboard', { replace: true });
  }, 2000);
  
  return;
}

  // ============================================
  // CASE 4: NEW LISTING CREATION ⭐ THE FIX!
  // ============================================
  if (isListingCreation) {
    setStatusMessage('Payment completed! Creating your listing...');

    try {
      // STEP 1: Get pending listing data
      const pendingListingStr = sessionStorage.getItem('pendingListing');
      const formDataStr = sessionStorage.getItem('createListingFormData');

      if (!pendingListingStr) {
        throw new Error('No pending listing found');
      }

      const pendingListing = JSON.parse(pendingListingStr);
      const formData = formDataStr ? JSON.parse(formDataStr) : null;

      console.log('[PaymentStatus] Creating listing from saved data:', pendingListing);

      // STEP 2: Upload images from form data
     
let uploadedImageUrls = [];

if (formData?.imagePreviews && formData.imagePreviews.length > 0) {
  console.log('[PaymentStatus] Uploading', formData.imagePreviews.length, 'images...');
  
  // Convert base64 to File objects
  const imageFiles = await Promise.all(
    formData.imagePreviews.map(async (base64, index) => {
      const response = await fetch(base64);
      const blob = await response.blob();
      return new File([blob], `listing-image-${index}.jpg`, { type: 'image/jpeg' });
    })
  );

  // Upload each image
  const uploadPromises = imageFiles.map(async (file) => {
    const formData = new FormData();
    formData.append('image', file);  
    
    const uploadResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });

    if (!uploadResponse.ok) {
      throw new Error('Image upload failed');
    }

    const result = await uploadResponse.json();
    return result.url;  
  });

  uploadedImageUrls = await Promise.all(uploadPromises);
  console.log('[PaymentStatus] Images uploaded:', uploadedImageUrls);
}
      // STEP 3: Create the listing with uploaded images
      const listingData = {
        ...pendingListing,
        imageUrl: uploadedImageUrls,
        plan: paymentDetails.tier
      };

      console.log('[PaymentStatus] Submitting listing to API...');

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5000/api'}/listings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(listingData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create listing');
      }

      const result = await response.json();
      console.log('[PaymentStatus] Listing created successfully:', result);

      // STEP 4: Clear all saved data
      

      // STEP 4.5: Clear all saved data FIRST
      sessionStorage.removeItem('pendingListing');
      sessionStorage.removeItem('createListingFormData');
      sessionStorage.removeItem('paymentDetails');
      sessionStorage.removeItem('selectedPlan');
      sessionStorage.removeItem('comingFromCheckout'); // ⭐ ADD: Clear checkout flag

      // ⭐ STEP 5: Refresh user data to get updated subscription
      await refreshUser();

      // STEP 6: Show success and redirect
      toast({
        title: 'Listing Created!',
        description: 'Your listing has been submitted for admin approval.',
      });

      setStatusMessage('Listing created successfully! Redirecting...');

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2000);

    } catch (error) {
      console.error('[PaymentStatus] Failed to create listing:', error);
      
      toast({
        variant: 'destructive',
        title: 'Listing Creation Failed',
        description: error.message || 'Please contact support with your payment receipt.',
      });

      setStatusMessage('Payment successful, but listing creation failed. Please contact support.');
      setStatus('FAILED');
    }

    return;
  }

  // ============================================
  // DEFAULT: Unknown payment type
  // ============================================
  console.warn('[PaymentStatus] Unknown payment type:', paymentDetails);
  
  toast({
    title: 'Payment Successful',
    description: 'Your payment was processed successfully.',
  });

  sessionStorage.removeItem('paymentDetails');

  setTimeout(() => {
    navigate('/dashboard', { replace: true });
  }, 2000);
};




  const handleFailure = (reason) => {
    console.log('[PaymentStatus] Payment failed:', reason);
    
    setStatus('FAILED');
    setStatusMessage(reason || 'Payment failed. Please try again.');

    // Clear intervals
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    toast({
      variant: 'destructive',
      title: 'Payment Failed',
      description: reason,
    });
  };

  const handleTimeout = () => {
    console.log('[PaymentStatus] Payment timeout');
    
    setStatus('TIMEOUT');
    setStatusMessage('Payment verification timed out. Please check your phone and try again if payment was not completed.');

    // Clear intervals
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

    toast({
      variant: 'destructive',
      title: 'Timeout',
      description: 'Payment verification timed out.',
    });
  };

  const handleTestComplete = async () => {
    try {
      console.log('[TEST] Manually completing payment:', paymentDetails.paymentId);

      const response = await api.post(`/payments/test-complete/${paymentDetails.paymentId}`);

      if (response.data.success) {
        handleSuccess(response.data.payment);
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: response.data.message,
        });
      }
    } catch (error) {
      console.error('[TEST] Failed to complete payment:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to complete test payment',
      });
    }
  };

  const handleRetry = () => {
    navigate('/subscription/upgrade');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'PENDING':
        return <Loader2 className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 animate-spin text-blue-600" />;
      case 'COMPLETED':
        return <CheckCircle className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-green-600" />;
      case 'FAILED':
      case 'TIMEOUT':
        return <XCircle className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-red-600" />;
      default:
        return <Clock className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 text-gray-400" />;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/* CSS FIX 1: Page container - py-12 md:py-20 → py-6 sm:py-8 md:py-12 + px-4 */}
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        {/* CSS FIX 2: Card wrapper - add px-4 for mobile margins */}
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            {/* CSS FIX 3: CardHeader padding */}
            <CardHeader className="text-center p-3 sm:p-4 md:p-6">
              {/* CSS FIX 4: CardTitle sizing - text-3xl → text-lg sm:text-xl md:text-2xl lg:text-3xl */}
              <CardTitle className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold">
                Payment Status
              </CardTitle>
              {/* CSS FIX 5: CardDescription sizing */}
              <CardDescription className="text-xs sm:text-sm md:text-base">
                {status === 'PENDING' && 'Please complete the payment on your phone'}
                {status === 'COMPLETED' && 'Payment successful!'}
                {status === 'FAILED' && 'Payment failed'}
                {status === 'TIMEOUT' && 'Payment verification timed out'}
              </CardDescription>
            </CardHeader>

            {/* CSS FIX 6: CardContent padding */}
            {/* CSS FIX 7: Content spacing - space-y-6 → space-y-3 sm:space-y-4 md:space-y-6 */}
            <CardContent className="p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6">
              {/* Status Icon */}
              {/* CSS FIX 8: Icon wrapper padding - py-8 → py-4 sm:py-6 md:py-8 */}
              <div className="flex justify-center py-4 sm:py-6 md:py-8">
                {/* CSS FIX 9: Icon sizes handled in getStatusIcon() function */}
                {getStatusIcon()}
              </div>

              {/* Status Message */}
              <div className="text-center">
                {/* CSS FIX 10: Message text sizing - text-lg → text-sm sm:text-base md:text-lg */}
                <p className="text-sm sm:text-base md:text-lg font-medium">{statusMessage}</p>
              </div>

              {/* Payment Details */}
              {paymentDetails && (
                <div className="bg-muted p-3 sm:p-4 rounded-lg space-y-1.5 sm:space-y-2">
                  <div className="flex justify-between">
                    {/* CSS FIX 11: Label text sizing - text-sm → text-xs sm:text-sm */}
                    <span className="text-xs sm:text-sm text-muted-foreground">Plan:</span>
                    {/* CSS FIX 12: Value text sizing - text-sm → text-xs sm:text-sm */}
                    <span className="text-xs sm:text-sm font-medium">{paymentDetails.tier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs sm:text-sm text-muted-foreground">Amount:</span>
                    <span className="text-xs sm:text-sm font-medium">
                      KES {paymentDetails.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-xs sm:text-sm text-muted-foreground">Phone:</span>
                    <span className="text-xs sm:text-sm font-medium">{paymentDetails.phoneNumber}</span>
                  </div>
                </div>
              )}

              {/* Countdown Timer (only show when pending) */}
              {status === 'PENDING' && (
                <div className="text-center">
                  {/* CSS FIX 13: Countdown text sizing */}
                  <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                    Time remaining: <span className="font-mono font-bold">{formatTime(countdown)}</span>
                  </p>
                </div>
              )}

              {/* Instructions */}
              {status === 'PENDING' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  {/* CSS FIX 14: Instructions heading sizing - text-sm → text-xs sm:text-sm */}
                  <h4 className="font-semibold text-xs sm:text-sm mb-2">Next steps:</h4>
                  {/* CSS FIX 15: Instructions list text sizing - text-sm → text-xs sm:text-sm */}
                  <ul className="text-xs sm:text-sm space-y-1 text-muted-foreground">
                    <li>1. Check your phone for M-Pesa prompt</li>
                    <li>2. Enter your M-Pesa PIN</li>
                    <li>3. Wait for confirmation (this page will update automatically)</li>
                  </ul>
                </div>
              )}

              {/* TEST MODE BUTTON */}
              {import.meta.env.DEV && (status === 'PENDING' || status === 'FAILED') && (
                <div className="border-2 border-orange-500 rounded-lg p-3 sm:p-4 bg-orange-50">
                  {/* CSS FIX 16: Test mode heading - text-sm → text-xs sm:text-sm */}
                  <p className="text-xs sm:text-sm font-semibold text-orange-800 mb-2">
                    🧪 Development Mode
                  </p>
                  {/* CSS FIX 17: Test mode description - text-xs → text-[10px] sm:text-xs */}
                  <p className="text-[10px] sm:text-xs text-orange-700 mb-3">
                    M-Pesa sandbox not working? Click below to complete payment manually
                  </p>
                  {/* CSS FIX 18: Test button sizing */}
                  <Button
                    variant="outline"
                    className="w-full border-orange-500 text-orange-700 hover:bg-orange-100 text-xs sm:text-sm h-10 sm:h-11"
                    onClick={handleTestComplete}
                  >
                    Complete Payment (TEST)
                  </Button>
                </div>
              )}

              {/* Action Buttons */}
              {(status === 'FAILED' || status === 'TIMEOUT') && (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
                  {/* CSS FIX 19: Button sizing */}
                  <Button
                    variant="outline"
                    className="flex-1 text-xs sm:text-sm h-10 sm:h-11"
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    className="flex-1 text-xs sm:text-sm h-10 sm:h-11"
                    onClick={handleRetry}
                  >
                    Try Again
                  </Button>
                </div>
              )}

              {status === 'COMPLETED' && (
                <div className="text-center text-xs sm:text-sm text-muted-foreground">
                  Redirecting to dashboard...
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
